import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { createNewPost } from "store/actions";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { phaseOptions, topicOptions } from "utils/constants";
import ImageCropModal from "./ImageCropModal";
const CreatePostModal = ({ open, onClose, onPostCreated }) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [topicTag, setTopicTag] = useState();
  const [phaseTag, setPhaseTag] = useState();
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  // Image-crop flow
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState("");
  const [cropFileName, setCropFileName] = useState("");
  const dispatch = useDispatch();

  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = "Title is required";
    }

    // if (!content.trim()) {
    //   newErrors.content = "Content is required";
    // }


    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    // Reset the input so selecting the same file again re-triggers onChange
    e.target.value = "";
    if (!selectedFile) return;

    setErrors((prev) => ({ ...prev, file: "" }));

    if (selectedFile.type.startsWith("image/")) {
      // Open the crop dialog for images
      const src = URL.createObjectURL(selectedFile);
      setCropSrc(src);
      setCropFileName(selectedFile.name);
      setCropOpen(true);
    } else {
      // Videos (and anything non-image) bypass cropping
      applyFile(selectedFile);
    }
  };

  const applyFile = (newFile) => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(newFile);
    setFileName(newFile.name);
    setPreviewUrl(
      newFile.type.startsWith("image/") ? URL.createObjectURL(newFile) : ""
    );
  };

  const handleCropComplete = (croppedFile) => {
    applyFile(croppedFile);
    closeCrop();
  };

  const closeCrop = () => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc("");
    setCropFileName("");
    setCropOpen(false);
  };

  const handleSubmit = () => {
    if (!validateForm()) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("title", title);
    formData.append("content", content);

    if (topicTag) {
      formData.append("topicTag", topicTag);
    }

    if (phaseTag) {
      formData.append("phaseTag", phaseTag);
    }

    if (file) {
      formData.append("file", file);
    }

    dispatch(
      createNewPost(formData, (result) => {
        setLoading(false);
        if (result?.code === 201 || result?.code === 200) {
          resetForm();
          onPostCreated();
        }
      })
    );
  };


  const resetForm = () => {
    setTitle("");
    setContent("");
    setTopicTag("");
    setPhaseTag("");
    setFile(null);
    setFileName("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Post</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Title"
            fullWidth
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (errors.title) setErrors((prev) => ({ ...prev, title: "" }));
            }}
            error={Boolean(errors.title)}
            helperText={errors.title}
            placeholder="Enter post title"
            multiline
            rows={2}
          />

          <TextField
            label="Content"
            fullWidth
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              if (errors.content) setErrors((prev) => ({ ...prev, content: "" }));
            }}
            // error={Boolean(errors.content)}
            // helperText={errors.content}
            placeholder="Enter post content"
            multiline
            rows={4}
          />

          <FormControl fullWidth error={Boolean(errors.topicTag)}>
            <InputLabel>Topic Tag (Optional)</InputLabel>
            <Select
              value={topicTag}
              label="Topic Tag (Optional)"
              onChange={(e) => setTopicTag(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {topicOptions.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth error={Boolean(errors.phaseTag)}>
            <InputLabel>Phase Tag (Optional)</InputLabel>
            <Select
              value={phaseTag}
              label="Phase Tag (Optional)"
              onChange={(e) => setPhaseTag(e.target.value)}
            >
              <MenuItem value="">None</MenuItem>
              {phaseOptions.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              border: "2px dashed #ccc",
              borderRadius: 1,
              p: 2,
              textAlign: "center",
              cursor: "pointer",
              transition: "border-color 0.3s",
              backgroundColor: file ? "#f0f7ff" : "#fafafa",
              // borderColor: errors.file ? "#d32f2f" : file ? "#1976d2" : "#ccc",
              // "&:hover": {
              //   borderColor: "#1976d2",
              // },
            }}
            component="label"
          >
            <input
              hidden
              accept="image/*,video/*"
              type="file"
              onChange={handleFileChange}
            />
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="preview"
                  sx={{
                    maxHeight: 160,
                    maxWidth: "100%",
                    borderRadius: 1,
                    mb: 1,
                    objectFit: "contain",
                  }}
                />
              ) : (
                <CloudUploadIcon sx={{ fontSize: 32, color: "primary.main", mb: 1 }} />
              )}
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {fileName || "Click to upload or drag and drop"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Supported formats: images, videos (Max 50MB)
              </Typography>
            </Box>
          </Box>
          {errors.file && (
            <Typography variant="caption" color="error">
              {errors.file}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          className="create-btn"
          disabled={loading}
          sx={{ position: "relative" }}
        >
          {loading ? (
            <>
              <CircularProgress
                size={20}
                sx={{
                  position: "absolute",
                  left: "50%",
                  marginLeft: "-10px",
                }}
              />
              <span style={{ visibility: "hidden" }}>Save</span>
            </>
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>

    <ImageCropModal
      open={cropOpen}
      imageSrc={cropSrc}
      fileName={cropFileName}
      onCancel={closeCrop}
      onCropComplete={handleCropComplete}
    />
    </>
  );
};

export default CreatePostModal;
